import { useRef, useState, useEffect, useContext } from "react";
import InnerLoader from "../loader/inner-loader";
import { useApiCall } from "../../custom-hooks/api-call";
import { GlobalContext } from "../../../context/Context";

export default function AudioPlayer({ src, onClose, onPlayChange, onTimeUpdate }) {
  let { state } = useContext(GlobalContext)
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const apiCall = useApiCall();

  /* 🔹 Fetch audio as BLOB (FIXES remote MP3 issue) */
  useEffect(() => {
    let objectUrl;
    if (!src) return;

    setIsLoading(true); // 🔹 new: src change hone par loader show hoga
    setAudioUrl(null);  // reset previous audio

    fetch(src, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Audio fetch failed");
        return res.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setAudioUrl(objectUrl);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, state.token]);

  /* 🔹 Audio events */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => setDuration(audio.duration || 0);
    // const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onTimeUpdateHandler = () => {
      const time = audio.currentTime;
      setCurrentTime(time);
    
      // 🔥 send time to parent (milliseconds)
      onTimeUpdate && onTimeUpdate(time * 1000);
    };

    // 🔹 Important: Track actual play/pause events
    const onPlay = () => {
      setIsPlaying(true);
      onPlayChange && onPlayChange(true);
    };
    const onPause = () => {
      setIsPlaying(false);
      onPlayChange && onPlayChange(false);
    };
    const onEnded = () => {
      setIsPlaying(false);
      onPlayChange && onPlayChange(false);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTimeUpdateHandler);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTimeUpdateHandler);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [audioUrl]);


  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);

    onPlayChange && onPlayChange(!isPlaying);
  };

  const handleSeek = (e) => {
    if (!duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percent * duration;
  };

  const formatTime = (time) => {
    if (!time) return "0:00";
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60).toString().padStart(2, "0");
    return `${min}:${sec}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          onPlayChange && onPlayChange(true);
        })
        .catch(() => {
          setIsPlaying(false);
          onPlayChange && onPlayChange(false);
        });
    } else {
      setIsPlaying(false);
    }
  }, [audioUrl]);

  return (
    <div className="w-full bg-white rounded-xl shadow-md px-4 py-3 flex items-center gap-4">
      {/* Audio */}
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}

      {/* Play / Pause */}
      <button
        onClick={togglePlay}
        disabled={!audioUrl}
        className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white disabled:opacity-50"
      >
        {
          isLoading ?
            <InnerLoader textStatus={false} />
            :
            isPlaying ? "❚❚" : "▶"
        }
        {/* {isPlaying ? "❚❚" : "▶"} */}
      </button>

      {/* Current Time */}
      <span className="text-sm text-gray-500 w-12">
        {formatTime(currentTime)}
      </span>

      {/* Progress Bar */}
      <div
        ref={progressRef}
        onClick={handleSeek}
        className="relative flex-1 h-2 bg-gray-200 rounded-full cursor-pointer"
      >
        <div
          className="absolute top-0 left-0 h-2 bg-blue-500 rounded-full"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 rounded-full"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>

      {/* Duration */}
      <span className="text-sm text-gray-500 w-12">
        {formatTime(duration)}
      </span>

      {/* Close */}
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 text-xl"
      >
        ×
      </button>
    </div>
  );
}
